<template>
  <modal :show="true" :large="true" v-on:close="closeModal">
    <div class="d-flex justify-content-between w-100" slot="title">
      <h4>Add Rider</h4>
      <button
        class="btn-primary btn btn-sm"
        type="button"
        v-on:click="copyClientDetails"
        v-if="client"
      >
        Copy Client Details
      </button>
    </div>
    <div class="row">
      <div class="col-xl-12">
        <div class="form-horizontal">
          <selectlist
            name="type_id"
            :options="types"
            v-model="driver.type_id"
            label="Driver Type"
            placeholder="Please Select"
            :required="true"
            helper="Please select whether this is the main driver or additional driver"
            :show-error="driver.errors.has('type_id')"
            error-message="The driver type is required"
          ></selectlist>

          <inputbox
            v-model="driver.name"
            name="name"
            label="Driver Name"
            helper="Please enter the name of the driver, as it is on their driving licence"
          ></inputbox>

          <date-of-birth
            v-model="driver.date_of_birth"
            helper="Please enter the date of birth of the driver"
            name="date_of_birth"
          ></date-of-birth>

          <spinner
            v-model="driver.convictions"
            label="Convictions:"
            helper="Please set the total number of convictions this driver has"
          ></spinner>

          <spinner
            v-model="driver.points"
            label="Points:"
            helper="Please set the total number of points this driver has"
          ></spinner>

          <input-block
            name="conviction_details"
            label="Conviction Details"
            helper="If this driver has had previous convictions, please enter the details about those here"
            v-model="driver.conviction_details"
          ></input-block>

          <spinner
            v-model="driver.accidents_in_three_years"
            helper="Please set the total number of accidents this drive has had within the past three years"
            label="Accidents Within The Last Three Years:"
          ></spinner>

          <inputbox
            name="driving_licence"
            label="Driving Licence Number"
            helper="Please enter the driving licence number for the proposed driver"
            v-model="driver.licence_number"
            :required="true"
            :show-error="driver.errors.has('licence_number')"
            error-message="The driving licence number is required"
          ></inputbox>

          <div
            class="form-group has-helper"
            :class="{ 'has-error': driver.errors.has('issue_date') }"
          >
            <div class="col-lg-4">
              <label for="issued_at" class="control-label">Issue Date: *</label>
              <p class="help-block">
                Please enter the date that the proposed drivers licence was
                issued
              </p>
            </div>
            <div class="col-lg-8">
              <datepicker
                name="issue_date"
                format="dd/MM/yyyy"
                :value="driver.issue_date"
                v-on:selected="setIssuedAtDate"
                input-class="form-control"
                placeholder="dd/mm/yyyy"
              ></datepicker>
              <span
                class="text-danger"
                v-text="driver.errors.get('issue_date')"
                v-if="driver.errors.has('issue_date')"
              ></span>
            </div>
          </div>

          <div
            class="form-group has-helper"
            :class="{ 'has-error': driver.errors.has('expiry_date') }"
          >
            <div class="col-lg-4">
              <label for="expires_at" class="control-label"
                >Expiry Date: *</label
              >
              <p class="help-block">
                Please enter the date that the proposed drivers licence expires
              </p>
            </div>
            <div class="col-lg-8">
              <datepicker
                name="expiry_date"
                format="dd/MM/yyyy"
                :value="driver.expiry_date"
                v-on:selected="setExpiresAtDate"
                input-class="form-control"
                placeholder="dd/mm/yyyy"
              ></datepicker>
              <span
                class="text-danger"
                v-text="driver.errors.get('expiry_date')"
                v-if="driver.errors.has('expiry_date')"
              ></span>
            </div>
          </div>

          <toggleswitch
            :labels="true"
            name="has_uk_licence"
            label="UK Licence?"
            helper="Does the proposed driver have a valid UK Licence?"
            v-model="driver.has_uk_licence"
          ></toggleswitch>

          <toggleswitch
            :labels="true"
            name="has_new_licence"
            label="Licence Less Than 12 Months Old?"
            helper="Is the proposed drivers licence less than 12 months old?"
            v-model="driver.has_new_licence"
          ></toggleswitch>
        </div>
      </div>
    </div>
    <div slot="footer">
      <button type="button" v-on:click="closeModal" class="btn btn-danger">
        Close
      </button>
      <form-button :indicator="driver.processing()" v-on:clicked="addDriver"
        >Add New Driver</form-button
      >
    </div>
  </modal>
</template>
<script>
export default {
  props: {
    incident: {
      type: String,
      required: true
    },

    hire: {
      type: String
    },

    types: {
      type: Array,
      required: true
    },

    client: {
      type: Object
    }
  },

  data() {
    return {
      driver: new window.Form({
        type_id: null,
        name: null,
        date_of_birth: window.DateManager.driverStartingDate(),
        convictions: 0,
        points: 0,
        conviction_details: null,
        accidents_in_three_years: 0,
        licence_number: null,
        issue_date: null,
        expiry_date: null,
        city: null,
        had_proposal_declined: false,
        has_uk_licence: false,
        has_new_licence: false
      })
    };
  },

  methods: {
    copyClientDetails() {
      if (this.client) {
        let name = '';

        if (this.client.first_name) {
          name += this.client.first_name + ' ';
        }

        if (this.client.last_name) {
          name += this.client.last_name;
        }

        this.driver.name = name;
        this.driver.date_of_birth = this.client.date_of_birth
          ? window.DateManager.format(this.client.date_of_birth)
          : null;
        this.driver.licence_number = this.client.indemnity?.license_number;

        window.toastify.success(
          'Complete',
          "We've copied those details for you."
        );
      }
    },

    closeModal() {
      this.$emit('close');
    },

    addDriver() {
      this.driver
        .post('/hires/' + this.hire + '/drivers')
        .then(response => {
          this.$emit('added', response);
        })
        .catch(error => {
          window.toastify.error(
            'Error',
            'Sorry, there was an error creating the driver.'
          );
        });
    },

    setIssuedAtDate(date) {
      this.driver.issue_date = date;
      let expiryDate = new Date(date.getTime());
      expiryDate.setFullYear(date.getFullYear() + 10);
      this.setExpiresAtDate(expiryDate);
    },

    setExpiresAtDate(date) {
      this.driver.expiry_date = date;
    }
  }
};
</script>
